import { useCallback, useRef, useState } from 'react';

export type UpdateState =
  | 'idle'
  | 'checking'
  | 'available'
  | 'up-to-date'
  | 'downloading'
  | 'ready'
  | 'blocked-by-recording'
  | 'error';

type DownloadEvent =
  | { event: 'Started'; data: { contentLength?: number } }
  | { event: 'Progress'; data: { chunkLength: number } }
  | { event: 'Finished' };

interface PendingUpdate {
  version: string;
  body?: string;
  downloadAndInstall: (onEvent: (e: DownloadEvent) => void) => Promise<void>;
}

interface Options {
  isRecording: boolean;
}

/**
 * Manual update check for the desktop build.
 *
 * Checks run only when the user asks — there is no poll on mount. The update
 * endpoint is baked into the binary at build time, so a staging install only
 * ever sees staging releases.
 */
export function useAppUpdate({ isRecording }: Options) {
  const [state, setState] = useState<UpdateState>('idle');
  const [availableVersion, setAvailableVersion] = useState<string | null>(null);
  const [notes, setNotes] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const pending = useRef<PendingUpdate | null>(null);

  const check = useCallback(async () => {
    setState('checking');
    try {
      const { check: checkForUpdate } = await import('@tauri-apps/plugin-updater');
      const update = (await checkForUpdate()) as PendingUpdate | null;
      if (!update) {
        setState('up-to-date');
        return;
      }
      pending.current = update;
      setAvailableVersion(update.version);
      setNotes(update.body ?? null);
      setState('available');
    } catch {
      setState('error');
    }
  }, []);

  const install = useCallback(async () => {
    const update = pending.current;
    if (!update) return;

    // A restart mid-capture loses the in-progress recording, so this blocks the
    // download outright rather than downloading now and deferring the relaunch.
    if (isRecording) {
      setState('blocked-by-recording');
      return;
    }

    setState('downloading');
    setProgress(0);
    try {
      let total = 0;
      let received = 0;
      await update.downloadAndInstall((e) => {
        if (e.event === 'Started') {
          total = e.data.contentLength ?? 0;
        } else if (e.event === 'Progress') {
          received += e.data.chunkLength;
          if (total > 0) setProgress(Math.round((received / total) * 100));
        }
      });
      setState('ready');
      const { relaunch } = await import('@tauri-apps/plugin-process');
      await relaunch();
    } catch {
      setState('error');
    }
  }, [isRecording]);

  return { state, availableVersion, notes, progress, check, install };
}
